import { RagApiClient } from '@/services/ApiService'

export interface AgentCalendarBinding {
  id: string
  idGestor: string
  agentDisplayName: string
  googleCalendarId: string
  personalCalendarIds: string[]
  timezone: string
  createdAt: string
  updatedAt: string
}

export interface UpsertAgentCalendarBindingPayload {
  idGestor: string
  agentDisplayName: string
  googleCalendarId: string
  personalCalendarIds?: string[]
  timezone?: string
}

export interface VisitDurationConfig {
  samePropertyMinutes: number
  differentPropertyMinutes: number
  // Disponibilidad: qué huecos se pueden ofrecer. Opcionales para tolerar un
  // backend anterior a la política (devolvería solo las dos duraciones).
  minNoticeHours?: number
  minNoticeBusinessHours?: boolean
  workdayMorningStart?: string
  workdayMorningEnd?: string
  workdayAfternoonStart?: string | null
  workdayAfternoonEnd?: string | null
  workdays?: number[]
}

export interface AgencyClosureDay {
  date: string
  reason: string
}

type BindingsListResponse = {
  bindings: AgentCalendarBinding[]
}

type ClosureDaysResponse = {
  closureDays: AgencyClosureDay[]
}
export type AgentExclusion = {
  id_gestor: string
  nombre: string
  motivo: string
  created_by: string
  created_at: string
}

export type NewAgentExclusion = Pick<AgentExclusion, 'id_gestor' | 'nombre' | 'motivo'>

type ExclusionsResponse = {
  exclusions: AgentExclusion[]
}

const VISIT_SCHEDULING_BASE = '/api/v1/visit-scheduling'

// Standard RAG bearer (same as rest of app) for all visit-scheduling endpoints including propose/confirm.
const visitSchedulingApi = new RagApiClient()

class VisitSchedulingServiceClient {
  async getConfig(): Promise<VisitDurationConfig> {
    return await visitSchedulingApi.get(`${VISIT_SCHEDULING_BASE}/config`) as VisitDurationConfig
  }

  async updateConfig(payload: VisitDurationConfig): Promise<VisitDurationConfig> {
    return await visitSchedulingApi.put(`${VISIT_SCHEDULING_BASE}/config`, payload) as VisitDurationConfig
  }

  async listClosureDays(): Promise<AgencyClosureDay[]> {
    const response = await visitSchedulingApi.get(`${VISIT_SCHEDULING_BASE}/closure-days/`) as ClosureDaysResponse
    return Array.isArray(response?.closureDays) ? response.closureDays : []
  }

  async addClosureDay(date: string, reason: string): Promise<AgencyClosureDay> {
    return await visitSchedulingApi.post(`${VISIT_SCHEDULING_BASE}/closure-days/`, { date, reason }) as AgencyClosureDay
  }

  async deleteClosureDay(date: string): Promise<void> {
    // La fecha va en el cuerpo, no en la query: el cliente añade una barra final
    // a la URL y `?date=2026-09-01` se convertiría en `?date=2026-09-01/`.
    // El endpoint acepta ambas formas.
    await visitSchedulingApi.delete(`${VISIT_SCHEDULING_BASE}/closure-days/`, { data: { date } })
  }

  async listBindings(): Promise<AgentCalendarBinding[]> {
    const response = await visitSchedulingApi.get(`${VISIT_SCHEDULING_BASE}/bindings`) as BindingsListResponse
    return Array.isArray(response?.bindings) ? response.bindings : []
  }

  async createBinding(payload: UpsertAgentCalendarBindingPayload): Promise<AgentCalendarBinding> {
    return await visitSchedulingApi.post(`${VISIT_SCHEDULING_BASE}/bindings`, payload) as AgentCalendarBinding
  }

  async updateBinding(bindingId: string, payload: Partial<UpsertAgentCalendarBindingPayload>): Promise<AgentCalendarBinding> {
    return await visitSchedulingApi.patch(`${VISIT_SCHEDULING_BASE}/bindings/${bindingId}`, payload) as AgentCalendarBinding
  }

  async deleteBinding(bindingId: string): Promise<void> {
    await visitSchedulingApi.delete(`${VISIT_SCHEDULING_BASE}/bindings/${bindingId}`)
  }

  async listExcludedAgents(): Promise<AgentExclusion[]> {
    const response = await visitSchedulingApi.get(`${VISIT_SCHEDULING_BASE}/excluded-agents/`) as ExclusionsResponse
    return Array.isArray(response?.exclusions) ? response.exclusions : []
  }

  async excludeAgent(payload: NewAgentExclusion): Promise<AgentExclusion> {
    return await visitSchedulingApi.post(`${VISIT_SCHEDULING_BASE}/excluded-agents/`, payload) as AgentExclusion
  }

  async readmitAgent(idGestor: string): Promise<void> {
    await visitSchedulingApi.delete(`${VISIT_SCHEDULING_BASE}/excluded-agents/${idGestor}/`)
  }

  async proposeSlots(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return await visitSchedulingApi.post(`${VISIT_SCHEDULING_BASE}/propose-slots`, payload) as Record<string, unknown>
  }

  async confirmBooking(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return await visitSchedulingApi.post(`${VISIT_SCHEDULING_BASE}/confirm-booking`, payload) as Record<string, unknown>
  }
}

export const visitSchedulingService = new VisitSchedulingServiceClient()
